/**
 * DataBest — DatePickerUS
 *
 * Reusable date picker whose visible label ALWAYS reads MM/DD/YYYY,
 * regardless of the browser locale. Solves the `<input type="date">`
 * limitation (that native input follows the OS locale and cannot be forced).
 *
 * Value contract:
 *   - Prop `value`: ISO string "YYYY-MM-DD" or empty
 *   - Prop `onChange(iso)` called with ISO "YYYY-MM-DD" or empty string
 */
import React, { useState } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { formatDateUS } from "@/lib/dates";

const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) =>
    d
        ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        : "";
const fromIso = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return undefined;
    return new Date(`${iso}T00:00:00`);
};

const DatePickerUS = ({
    value,
    onChange,
    placeholder = "MM/DD/YYYY",
    disabled = false,
    min,
    max,
    className = "",
    testId,
    clearable = false,
    align = "start",
}) => {
    const [open, setOpen] = useState(false);
    const selected = fromIso(value);
    const label = value ? formatDateUS(value) : placeholder;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={disabled}
                    data-testid={testId}
                    className={`inline-flex h-8 min-w-[130px] items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:border-slate-300 focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
                >
                    <span className={value ? "" : "text-slate-400"}>{label}</span>
                    <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-0"
                align={align}
                data-testid={testId ? `${testId}-popover` : undefined}
            >
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={(d) => {
                        onChange(toIso(d));
                        setOpen(false);
                    }}
                    fromDate={fromIso(min)}
                    toDate={fromIso(max)}
                    initialFocus
                />
                {clearable && value && (
                    <div className="flex justify-end border-t border-slate-100 p-2">
                        <button
                            type="button"
                            onClick={() => {
                                onChange("");
                                setOpen(false);
                            }}
                            data-testid={testId ? `${testId}-clear` : undefined}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        >
                            <X className="h-3 w-3" /> Clear
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};

export default DatePickerUS;
